import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QwenCommandDto, QwenService } from '../qwen/qwen.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';

const SECONDARY_STATUS_CLASS = 'bg-secondary';

@Component({
  selector: 'app-qwen-command-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './qwen-command-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './qwen-command-list.component.scss'
})
export class QwenCommandListComponent {
  @Input() commands: QwenCommandDto[] = [];
  @Output() onDeleted = new EventEmitter<void>();

  expandedId: number | null = null;

  private readonly gitlabStatusColors: Record<string, string> = {
    'opened': 'bg-success',
  };

  private readonly strapiStatusColors: Record<string, string> = {
    'completelyOpen': 'bg-success',
  };

  constructor(
    private qwenService: QwenService,
    private toastr: ToastrService
  ) {}

  getGitlabStatusClass(status: string | null): string {
    if (!status) {
      return SECONDARY_STATUS_CLASS;
    }
    return this.gitlabStatusColors[status] || SECONDARY_STATUS_CLASS;
  }

  getStrapiStatusClass(status: string | null): string {
    if (!status) {
      return SECONDARY_STATUS_CLASS;
    }
    return this.strapiStatusColors[status] || SECONDARY_STATUS_CLASS;
  }

  toggleExpand(id: number): void {
    if (this.expandedId === id) {
      this.expandedId = null;
    } else {
      this.expandedId = id;
    }
  }

  deleteCommand(id: number, event: MouseEvent): void {
    event.stopPropagation();
    this.qwenService.deleteQwenCommand(id).subscribe({
      next: () => {
        this.toastr.success('Command deleted successfully');
        this.onDeleted.emit();
      },
      error: (err) => {
        this.toastr.error('Failed to delete command: ' + (err.error?.message || err.message));
      }
    });
  }

  deleteObsoleteMrRelated(): void {
    const obsoleteCommands = this.commands.filter(cmd =>
      cmd.gitlabMrWebUrl !== null &&
      cmd.gitlabMrGitlabStatus === null &&
      cmd.gitlabMrStrapiStatus === null
    );

    if (obsoleteCommands.length === 0) {
      this.toastr.info('No obsolete MR related commands found');
      return;
    }

    const deleteObservables = obsoleteCommands.map(cmd => this.qwenService.deleteQwenCommand(cmd.id));

    forkJoin(deleteObservables).subscribe({
      next: () => {
        this.toastr.success(`${obsoleteCommands.length} commands deleted successfully`);
        this.onDeleted.emit();
      },
      error: (err) => {
        this.toastr.error('Failed to delete some commands: ' + (err.error?.message || err.message));
        this.onDeleted.emit(); // Emit anyway to refresh the list with whatever was deleted
      }
    });
  }
}
